import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { usePermission } from '../hooks/usePermission';
import { useToast } from './ui/Toast';
import { ErrorNotice, Loading } from './ui';

interface BoardRow {
  metalId: string;
  metalCode: string;
  purityId: string | null;
  purityCode: string | null;
  rateType: 'SALE' | 'PURCHASE';
  currentRate: string | null;
  effectiveFrom: string | null;
  isToday: boolean;
}

const DISMISS_KEY = 'aurum.rateboard.dismissed';

/**
 * Today on the shop's own clock. toISOString() is UTC, which in India is still
 * yesterday until 5:30 in the morning — the board then forgot it had been
 * dismissed and opened again.
 */
function localDay(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** True once today's rate board has been saved or dismissed, so it opens once a day. */
function dismissedToday(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === localDay();
  } catch {
    return false;
  }
}
function markDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, localDay());
  } catch {
    /* private window: the board may open again, which is harmless */
  }
}

/**
 * A rate exactly as published — 98.5 stays 98.5. Rounding it to rupees put
 * "99" in the box, and saving the board unchanged then moved the rate.
 */
const exact = (v: string): string => String(Number(v));

/**
 * The morning rate board.
 *
 * A jeweller sets the day's rates before opening, and a shop with no rate for
 * today cannot sell. So this appears once each morning — and any time the rate
 * moves and someone opens it — as a single board: every metal and purity the
 * shop prices, the rate live for each, and a box to type the new one. Saving
 * publishes them (the owner approves their own), and the shop is trading.
 */
export function RateMaster({
  open,
  onClose,
  manual,
}: {
  open: boolean;
  onClose: () => void;
  /** Opened from the rates page rather than by the morning prompt. */
  manual?: boolean;
}) {
  const { can } = usePermission();
  const client = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['rate-board'],
    queryFn: () => api.get<{ data: BoardRow[] }>('/rates/board'),
    enabled: open,
  });

  const rows = data?.data ?? [];
  const keyOf = (r: BoardRow): string => `${r.metalId}:${r.purityId ?? 'none'}:${r.rateType}`;

  // Seed the boxes with today's rate where it exists, so unchanged rates carry
  // forward and only real changes are typed.
  useEffect(() => {
    if (rows.length === 0) return;
    const seed: Record<string, string> = {};
    for (const r of rows) {
      seed[keyOf(r)] = r.isToday && r.currentRate ? exact(r.currentRate) : '';
    }
    setDraft(seed);
  }, [data]);

  const save = useMutation({
    mutationFn: () => {
      const entries = rows
        .map((r) => ({
          metalId: r.metalId,
          purityId: r.purityId,
          rateType: r.rateType,
          rate: draft[keyOf(r)] ?? '',
        }))
        .filter((e) => e.rate && Number(e.rate) > 0);
      return api.post<{ published: number; drafted: number }>('/rates/board', { entries });
    },
    onSuccess: (r) => {
      toast(
        r.published > 0 ? `${r.published} rates published` : `${r.drafted} rates saved for approval`,
        r.published > 0 ? 'The shop can price against these now' : 'A second person needs to approve them',
      );
      markDismissed();
      void client.invalidateQueries({ queryKey: ['rates'] });
      void client.invalidateQueries({ queryKey: ['rate-board'] });
      onClose();
    },
    onError: setError,
  });

  if (!open) return null;

  const saleRows = rows.filter((r) => r.rateType === 'SALE');
  const buyRows = rows.filter((r) => r.rateType === 'PURCHASE');
  const canEdit = can('rate.create');

  const row = (r: BoardRow): JSX.Element => {
    const k = keyOf(r);
    const stale = r.currentRate && !r.isToday;
    return (
      <div key={k} className="flex items-center gap-3 border-b border-silver-100/70 px-1 py-1.5 last:border-0">
        <span className="w-40 text-sm font-medium">
          {r.metalCode}
          {r.purityCode ? <span className="text-ink-soft"> {r.purityCode}</span> : null}
        </span>
        <span className="w-28 text-right text-2xs text-ink-soft">
          {r.currentRate ? (
            <>
              {r.isToday ? 'today' : 'was'}{' '}
              <span className="num">{Number(r.currentRate).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
              {stale ? <span className="block text-warn">{r.effectiveFrom}</span> : null}
            </>
          ) : (
            <span className="text-danger">not set</span>
          )}
        </span>
        <div className="relative flex-1">
          {canEdit ? (
            <>
              <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-ink-faint">
                ₹
              </span>
              <input
                className={`field num w-full pl-6 text-right ${draft[k] ? 'border-gold-500 bg-gold-50' : ''}`}
                inputMode="decimal"
                placeholder={r.currentRate ? exact(r.currentRate) : 'today'}
                value={draft[k] ?? ''}
                onChange={(e) => setDraft({ ...draft, [k]: e.target.value.replace(/[^\d.]/g, '') })}
              />
            </>
          ) : (
            // Someone who cannot set rates sees them, not boxes they cannot use.
            <p className="num py-1.5 text-right text-sm">
              {r.currentRate ? `₹${Number(r.currentRate).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—'}
            </p>
          )}
        </div>
        <span className="w-14 text-2xs text-ink-faint">{r.rateType === 'PURCHASE' ? '/fine g' : '/gram'}</span>
      </div>
    );
  };

  const changed = rows.filter((r) => draft[keyOf(r)] && Number(draft[keyOf(r)]) > 0).length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-rail/40 p-4 pt-[8vh]">
      <div className="w-full max-w-lg overflow-hidden rounded-xl bg-surface shadow-pop">
        <div className="flex items-center justify-between bg-rail px-5 py-3">
          <div>
            <h2 className="text-base font-bold text-rail-ink">Today's rates</h2>
            <p className="text-2xs text-rail-ink/75">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long' })}
            </p>
          </div>
          <button
            className="rounded px-2 py-1 text-2xs text-rail-ink/80 hover:bg-rail-soft"
            onClick={() => {
              // Only the morning prompt is silenced by "later"; opening the
              // board by hand and closing it should not stop tomorrow's.
              if (!manual) markDismissed();
              onClose();
            }}
          >
            {manual ? 'Close' : 'Later'}
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-5 py-3">
          <ErrorNotice error={error} />
          {isLoading ? <Loading rows={4} /> : null}

          {saleRows.length > 0 ? (
            <>
              <p className="mb-1 mt-1 text-2xs font-semibold uppercase tracking-wide text-ink-soft">Selling rate</p>
              {saleRows.map(row)}
            </>
          ) : null}

          {buyRows.length > 0 ? (
            <>
              <p className="mb-1 mt-4 text-2xs font-semibold uppercase tracking-wide text-ink-soft">
                Buying rate (old gold)
              </p>
              {buyRows.map(row)}
            </>
          ) : null}
        </div>

        {canEdit ? (
          <div className="flex items-center gap-3 border-t border-silver-100 bg-silver-50 px-5 py-3">
            <span className="text-2xs text-ink-soft">
              {changed === 0 ? 'Type the rates that changed' : `${changed} rate${changed === 1 ? '' : 's'} to publish`}
            </span>
            <button
              className="btn-primary ml-auto"
              disabled={changed === 0 || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'Publishing…' : 'Publish rates'}
            </button>
          </div>
        ) : (
          <div className="border-t border-silver-100 bg-silver-50 px-5 py-3 text-2xs text-ink-soft">
            You can see today's rates. Setting them is the owner's to do.
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Whether the morning board should pop on its own.
 *
 * Only for someone who can set rates — a cashier cannot act on it — and only
 * when a purity the shop actually prices is not yet set for today. Purities
 * never given a rate (9K, 14K in a shop that sells neither) do not count, or
 * the board would open every morning however carefully it was filled in.
 */
export function useShouldPromptRates(rows: BoardRow[] | undefined, canSet: boolean): boolean {
  if (!canSet || !rows || rows.length === 0) return false;
  if (dismissedToday()) return false;
  const sale = rows.filter((r) => r.rateType === 'SALE');
  const priced = sale.filter((r) => r.currentRate);
  // A shop with no selling rate at all yet: the board is the place to start.
  if (priced.length === 0) return sale.length > 0;
  return priced.some((r) => !r.isToday);
}
